import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HelpCircle, CheckCircle2, ImageOff } from "lucide-react";
import { fetchDiagramSvg, fetchDiagramHelp } from "@/lib/api";

/**
 * Renders a Claude-generated SVG for a record_id and a "Help me read this" button.
 *
 * Props:
 *   recordId: string  (required — used as the SVG cache key)
 *   description: string  (passed through; backend already has it but kept for fallback context)
 *
 * Behaviour:
 *   - On mount: fetches the SVG (server-side cached). Shows shimmer while loading.
 *   - "Help me read this" button: calls a separate uncached endpoint and shows the
 *     2-3 sentence Claude reply in a soft card. Button label flips to "Got it" —
 *     tapping again dismisses the help text.
 */
export default function DiagramRenderer({ recordId, description }) {
  const [svg, setSvg] = useState(null);
  const [svgError, setSvgError] = useState(null);
  const [svgLoading, setSvgLoading] = useState(true);

  const [helpText, setHelpText] = useState(null);
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpError, setHelpError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!recordId) {
      setSvgLoading(false);
      return;
    }
    setSvgLoading(true);
    setSvg(null);
    setSvgError(null);
    setHelpText(null);
    setHelpError(null);
    fetchDiagramSvg(recordId)
      .then((res) => {
        if (cancelled) return;
        setSvg(res?.svg || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setSvgError(err?.response?.data?.detail || "Couldn't load diagram.");
      })
      .finally(() => {
        if (!cancelled) setSvgLoading(false);
      });
    return () => { cancelled = true; };
  }, [recordId]);

  const onHelp = async () => {
    if (helpText) {
      // "Got it" — dismiss
      setHelpText(null);
      return;
    }
    setHelpLoading(true);
    setHelpError(null);
    try {
      const res = await fetchDiagramHelp(recordId);
      setHelpText(res?.help_text || "");
    } catch (err) {
      setHelpError(err?.response?.data?.detail || "Couldn't load help.");
    } finally {
      setHelpLoading(false);
    }
  };

  return (
    <div className="mb-5" data-testid="diagram-renderer">
      <div className="rounded-2xl border border-sky-100 bg-white p-4 mb-3" data-testid="diagram-svg-frame">
        {svgLoading && (
          <div className="h-56 flex items-center justify-center" data-testid="diagram-loading">
            <div className="animate-pulse text-sky-300 text-sm font-semibold tracking-wider">Drawing…</div>
          </div>
        )}
        {!svgLoading && svgError && (
          <div className="h-40 flex flex-col items-center justify-center text-slate-400 gap-2" data-testid="diagram-error">
            <ImageOff className="w-7 h-7" />
            <span className="text-sm">{svgError}</span>
            {description && <span className="text-xs italic max-w-md text-center">{description}</span>}
          </div>
        )}
        {!svgLoading && svg && (
          // Claude-generated SVG. We extract <svg>…</svg> on the server and render inline.
          // eslint-disable-next-line react/no-danger
          <div className="diagram-svg [&_svg]:w-full [&_svg]:h-auto [&_svg]:max-h-80 [&_svg]:mx-auto" dangerouslySetInnerHTML={{ __html: svg }} data-testid="diagram-svg-content" />
        )}
      </div>

      {!svgLoading && svg && (
        <div className="flex flex-col items-start gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onHelp}
            disabled={helpLoading}
            className={`rounded-full border-2 font-semibold tap-44 px-5 py-2 ${helpText ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-sky-300 text-sky-700 hover:bg-sky-50"}`}
            data-testid="diagram-help-btn"
          >
            {helpLoading ? (
              <span className="flex items-center gap-2"><span className="w-3.5 h-3.5 rounded-full border-2 border-sky-300 border-t-transparent animate-spin" /> Reading…</span>
            ) : helpText ? (
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Got it</span>
            ) : (
              <span className="flex items-center gap-2"><HelpCircle className="w-4 h-4" /> Help me read this</span>
            )}
          </Button>

          {helpError && (
            <Card className="rounded-2xl p-4 bg-amber-50 border-amber-200 w-full" data-testid="diagram-help-error">
              <p className="text-sm text-amber-800">{helpError}</p>
            </Card>
          )}

          {helpText && (
            <Card className="rounded-2xl p-5 bg-sky-50/70 border-sky-100 w-full fade-up" data-testid="diagram-help-card">
              <p className="text-slate-700 text-base leading-relaxed">{helpText}</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
